import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse, BatchItemListResponse, BatchItemVm, BatchItemWhItemVm } from '@/api/types'

function unwrap<T>(payload: any): T {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data as T
    if ('data' in payload) return payload.data as T
  }
  return payload as T
}

function toArray(payload: any): any[] {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.result)) return payload.result
  return []
}

function toTotalCount(payload: any, fallback: number): number {
  const candidates = [
    payload?.totalCount,
    payload?.totalRecords,
    payload?.recordCount,
    payload?.count,
    payload?.pagination?.total,
  ]
  for (const c of candidates) {
    const n = Number(c)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

function asNumber(v: any, fallback = 0): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function asString(v: any): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v)
}

function asBool(v: any): boolean {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1
  if (typeof v === 'string') return v.toLowerCase() === 'true' || v === '1'
  return false
}

function mapWhItem(x: any): BatchItemWhItemVm {
  return {
    id: asNumber(x?.id ?? x?.whItemId ?? 0),
    itemId: asNumber(x?.itemId ?? 0),
    batchItemId: asNumber(x?.batchItemId ?? x?.batchId ?? 0),
    wareHouseId: asNumber(x?.wareHouseId ?? x?.warehouseId ?? 0),
    shelfId: x?.shelfId == null ? '' : asString(x.shelfId),
    availableQuantity: asNumber(x?.availableQuantity ?? x?.availableStock ?? 0),
    damagedStock: asNumber(x?.damagedStock ?? 0),
    expiredStock: asNumber(x?.expiredStock ?? 0),
    holdStock: asNumber(x?.holdStock ?? 0),
    isDeleted: x?.isDeleted ?? x?.IsDeleted ?? null,
  }
}

function mapBatchItem(x: any): BatchItemVm {
  return {
    id: asNumber(x?.id ?? x?.batchItemId ?? 0),
    code: asString(x?.code ?? x?.batchCode ?? ''),
    reference: asString(x?.reference ?? ''),
    itemId: asNumber(x?.itemId ?? 0),
    batchDate: asString(x?.batchDate ?? ''),
    availableQuantity: asNumber(x?.availableQuantity ?? x?.availableQty ?? 0),
    damagedStock: asNumber(x?.damagedStock ?? 0),
    holdStock: asNumber(x?.holdStock ?? 0),
    expiredStock: asNumber(x?.expiredStock ?? 0),
    expiryDate: asString(x?.expiryDate ?? ''),
    isActive: x?.isActive == null && x?.IsActive == null ? true : asBool(x?.isActive ?? x?.IsActive),
    unitSellingPrice: asNumber(x?.unitSellingPrice ?? x?.sellingPrice ?? 0),
    unitCostPrice: asNumber(x?.unitCostPrice ?? x?.costPrice ?? 0),
    isTaxable: asBool(x?.isTaxable),
    tax: asNumber(x?.tax ?? 0),
    availabilityStatus: asString(x?.availabilityStatus ?? ''),
    branchId: asNumber(x?.branchId ?? 0),
    whItems: toArray(x?.whItems).map(mapWhItem),
  }
}

export class BatchItemService {
  async getAll(branchId: number, pageNumber: number, pageSize: number): Promise<BatchItemListResponse> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.batchItem.getAll(branchId, pageNumber, pageSize))
    const payload = unwrap<any>(response.data)
    const arr = toArray(payload).map(mapBatchItem)
    const totalCount = toTotalCount(payload, arr.length)
    return { items: arr, totalCount }
  }

  async getById(id: number): Promise<BatchItemVm> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.batchItem.getById(id))
    const payload = unwrap<any>(response.data)
    return mapBatchItem(payload)
  }

  /** GET /api/BatchItem/GetWhItems?batchId= — lines for edit form (opening stock grid). */
  async getWhItemsByBatchId(batchId: number): Promise<BatchItemWhItemVm[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.batchItem.getWhItems(batchId))
    const payload = unwrap<any>(response.data)
    return toArray(payload).map(mapWhItem)
  }

  async save(payload: BatchItemVm): Promise<void> {
    await apiClient.post(endpoints.batchItem.save, payload)
  }

  async update(payload: BatchItemVm): Promise<void> {
    await apiClient.put(endpoints.batchItem.update, payload)
  }
}

export const batchItemService = new BatchItemService()

